import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, ArrowLeft, Sparkles, Wand2, AlertTriangle, Gauge } from "lucide-react";
import { getAiBudget } from "@/lib/admin.functions";
import { SectionTabs, AI_ESTIMATOR_TABS } from "@/components/admin/SectionTabs";

export const Route = createFileRoute("/_authenticated/admin/ai-usage")({
  head: () => ({ meta: [{ title: "AI Usage — Admin" }] }),
  component: AdminAiUsage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="py-32 text-center text-zinc-300 bg-[#030303] min-h-screen">
        <p className="mb-3">{error.message}</p>
        <button onClick={() => { router.invalidate(); reset(); }} className="bg-white text-zinc-950 px-5 py-2 rounded-full text-sm font-semibold">Try again</button>
      </div>
    );
  },
  notFoundComponent: () => <div className="py-32 text-center text-zinc-400">Not found.</div>,
});

type Row = {
  id: string;
  kind: "segmentation" | "final_render" | "recolor";
  model: string | null;
  status: "ok" | "error";
  error: string | null;
  estimated_cost_credits: number;
  project_id: string | null;
  duration_ms: number | null;
  created_at: string;
};

const KIND_META: Record<Row["kind"], { label: string; icon: typeof Sparkles; color: string }> = {
  segmentation: { label: "Segmentation", icon: Sparkles, color: "text-blue-300" },
  final_render: { label: "Photorealistic render", icon: Wand2, color: "text-amber-200" },
  recolor: { label: "Recolor (legacy)", icon: Wand2, color: "text-zinc-300" },
};

function AdminAiUsage() {
  const budgetFn = useServerFn(getAiBudget);
  const budgetQuery = useQuery({
    queryKey: ["admin", "ai-budget"],
    queryFn: () => budgetFn(),
  });
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "ai-usage"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ai_usage_logs")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as Row[];
    },
  });

  const rows = data ?? [];
  const totals = rows.reduce(
    (acc, r) => {
      acc.count[r.kind] = (acc.count[r.kind] ?? 0) + 1;
      acc.cost[r.kind] = (acc.cost[r.kind] ?? 0) + Number(r.estimated_cost_credits);
      if (r.status === "error") acc.errors += 1;
      acc.totalCost += Number(r.estimated_cost_credits);
      return acc;
    },
    { count: {} as Record<string, number>, cost: {} as Record<string, number>, errors: 0, totalCost: 0 },
  );

  return (
    <section className="min-h-screen bg-[#030303] text-white">
      <div className="max-w-6xl mx-auto px-6 pt-16 pb-24">
        <div className="mb-6"><SectionTabs tabs={AI_ESTIMATOR_TABS} label="AI Estimator" /></div>
        <header className="mb-8">
          <p className="text-[11px] uppercase tracking-widest text-amber-200 mb-2">Admin</p>
          <h1 className="font-serif text-3xl md:text-4xl">AI Usage</h1>
          <p className="text-zinc-500 text-sm mt-1">Studio AI calls — last 500 events. Costs are estimates, not billed totals.</p>
        </header>

        {isLoading && (
          <div className="py-20 text-center text-zinc-500"><Loader2 className="size-5 animate-spin mx-auto" /></div>
        )}
        {error && (
          <div className="rounded-xl border border-red-400/30 bg-red-500/[0.06] p-4 text-sm text-red-200 mb-6 inline-flex items-center gap-2">
            <AlertTriangle className="size-4" /> {(error as Error).message}
          </div>
        )}

        {!isLoading && (
          <>
            {budgetQuery.data && (
              <div className="mb-6 grid sm:grid-cols-2 gap-3">
                {(["daily", "monthly"] as const).map((k) => {
                  const b = budgetQuery.data![k];
                  const over = k === "daily" ? budgetQuery.data!.overDaily : budgetQuery.data!.overMonthly;
                  const pct = Math.min(100, b.pct);
                  return (
                    <div key={k} className={`rounded-2xl border p-4 ${over ? "border-red-400/40 bg-red-500/[0.06]" : pct > 80 ? "border-amber-300/40 bg-amber-300/[0.06]" : "border-white/10 bg-white/[0.03]"}`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-zinc-300"><Gauge className="size-3" /> {k} budget</p>
                        <p className="text-xs text-zinc-400">{b.used.toFixed(1)} / {b.limit ?? "∞"} cr</p>
                      </div>
                      <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                        <div className={`h-full ${over ? "bg-red-400" : pct > 80 ? "bg-amber-300" : "bg-emerald-400"}`} style={{ width: `${pct}%` }} />
                      </div>
                      {over && <p className="mt-2 text-[11px] text-red-300">Studio AI is paused for new {k} renders until the limit resets or is raised in Settings.</p>}
                    </div>
                  );
                })}
              </div>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
              {(Object.keys(KIND_META) as Row["kind"][]).map((k) => {
                const meta = KIND_META[k];
                const Icon = meta.icon;
                return (
                  <div key={k} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                    <div className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest ${meta.color}`}>
                      <Icon className="size-3" /> {meta.label}
                    </div>
                    <p className="text-2xl font-serif mt-2">{totals.count[k] ?? 0}</p>
                    <p className="text-[11px] text-zinc-500 mt-1">~{(totals.cost[k] ?? 0).toFixed(1)} credits</p>
                  </div>
                );
              })}
              <div className="rounded-2xl border border-amber-300/30 bg-amber-300/[0.06] p-4">
                <p className="text-[10px] uppercase tracking-widest text-amber-200">Total estimated</p>
                <p className="text-2xl font-serif mt-2">~{totals.totalCost.toFixed(1)} cr</p>
                <p className="text-[11px] text-zinc-500 mt-1">{totals.errors} errors</p>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.03] text-[10px] uppercase tracking-widest text-zinc-500">
                  <tr>
                    <th className="text-left px-3 py-2">When</th>
                    <th className="text-left px-3 py-2">Kind</th>
                    <th className="text-left px-3 py-2">Model</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-right px-3 py-2">Duration</th>
                    <th className="text-right px-3 py-2">Est. cost</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={6} className="px-3 py-10 text-center text-zinc-500">No AI calls logged yet.</td></tr>
                  )}
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-white/5">
                      <td className="px-3 py-2 text-zinc-400 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                      <td className="px-3 py-2 text-zinc-200">{KIND_META[r.kind]?.label ?? r.kind}</td>
                      <td className="px-3 py-2 text-zinc-500 text-xs">{r.model ?? "—"}</td>
                      <td className="px-3 py-2">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full border ${r.status === "ok" ? "border-green-400/30 bg-green-400/10 text-green-300" : "border-red-400/30 bg-red-400/10 text-red-300"}`}>
                          {r.status}
                        </span>
                        {r.error && <p className="text-[10px] text-red-300/80 mt-1 truncate max-w-[280px]" title={r.error}>{r.error}</p>}
                      </td>
                      <td className="px-3 py-2 text-right text-zinc-400">{r.duration_ms != null ? `${(r.duration_ms / 1000).toFixed(1)}s` : "—"}</td>
                      <td className="px-3 py-2 text-right text-zinc-300">~{Number(r.estimated_cost_credits).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </section>
  );
}